import React, { useEffect, useMemo, useRef, useState } from "react";
import "./qualification.css";

const Qualification = () => {
  const [toggleState, setToggleState] = useState(1);
  const [activeItem, setActiveItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const sectionRef = useRef(null);

  const toggleTab = (index) => setToggleState(index);

  const education = useMemo(
    () => [
      {
        title: "B.Sc. Software Engineering",
        org: "Dominion University",
        date: "2021 – 2025",
        badge: "Degree",
        icon: "uil uil-graduation-cap",
        highlights: [
          "Software engineering fundamentals and professional development.",
          "Built real-world projects across frontend, backend and APIs.",
          "Team collaboration, delivery discipline and documentation.",
        ],
      },
      {
        title: "AI Engineering & MLOps",
        org: "DeepLearning.AI",
        date: "Certified",
        badge: "Certification",
        icon: "uil uil-robot",
        highlights: [
          "Applied AI engineering practices and production-minded workflows.",
          "Understanding model lifecycle, evaluation and deployment concepts.",
        ],
      },
      {
        title: "Google IT Automation with Python",
        org: "Google",
        date: "Certified",
        badge: "Certification",
        icon: "uil uil-code-branch",
        highlights: [
          "Automation scripting with Python and practical IT workflows.",
          "Process improvement mindset: automate repetitive tasks reliably.",
        ],
      },
      {
        title: "Agile Software Development Fundamentals",
        org: "Professional Certification",
        date: "Certified",
        badge: "Certification",
        icon: "uil uil-process",
        highlights: [
          "Agile fundamentals, delivery cycles and iterative development.",
          "Improved collaboration using structured planning and reviews.",
        ],
      },
      {
        title: "AWS Solutions Architect (Associate)",
        org: "Amazon Web Services",
        date: "In Progress",
        badge: "In Progress",
        icon: "uil uil-cloud",
        highlights: [
          "Cloud architecture foundations: compute, storage, networking.",
          "Designing scalable, secure systems with AWS services.",
        ],
      },
    ],
    []
  );

  const experience = useMemo(
    () => [
      {
        title: "Web Developer / Engineer",
        org: "Victory Christian College — Port Harcourt",
        date: "Aug 2019 – Jan 2020",
        badge: "Web",
        icon: "uil uil-globe",
        highlights: [
          "Designed and built a responsive website with clean UI structure.",
          "Improved visibility and communication through modern web presence.",
          "Handled updates, fixes and basic performance improvements.",
        ],
      },
      {
        title: "Head of Software Engineering",
        org: "OlgaCodes — Nigeria",
        date: "Jan 2020 – Present",
        badge: "Leadership",
        icon: "uil uil-layers",
        highlights: [
          "Led project delivery across frontend, backend and integrations.",
          "Built scalable web apps with focus on performance and clean UX.",
          "Mentored developers and maintained delivery quality standards.",
        ],
      },
      {
        title: "Technical Lead",
        org: "TSAG Foundation / Project (Non-Profit Technology & Charity Initiative)",
        date: "2022 – Present",
        badge: "NGO",
        icon: "uil uil-heart-medical",
        highlights: [
          "Built/managed digital tools that support charity and outreach efforts.",
          "Organized tech-driven initiatives to improve productivity and impact.",
          "Helped structure systems for community projects and engagement.",
        ],
      },
      {
        title: "ICT Staff (NYSC)",
        org: "National Board for Technology Incubation (NBTI) — Federal Government Agency",
        date: "2026",
        badge: "Public Sector",
        icon: "uil uil-desktop",
        highlights: [
          "Supported ICT operations and digital workflows for staff and programs.",
          "Assisted with troubleshooting, documentation, and process improvement.",
          "Contributed to tech incubation support initiatives and internal systems.",
        ],
      },
      {
        title: "Software Engineer (Automation & AI Integration)",
        org: "Intelligent Systems & Internal Platforms",
        date: "2023 – 2024",
        badge: "Automation",
        icon: "uil uil-bolt",
        highlights: [
          "Integrated automation and AI workflows into product/internal tools.",
          "Built robust API flows, validations, and backend logic for stability.",
          "Focused on reliability, maintainability, and delivery speed.",
        ],
      },
      {
        title: "Web Development Instructor",
        org: "VCC (Victory Christian College)",
        date: "Instructor Role",
        badge: "Teaching",
        icon: "uil uil-presentation-play",
        highlights: [
          "Taught web fundamentals and guided learners through real projects.",
          "Explained concepts clearly: HTML/CSS/JS + modern frontend patterns.",
        ],
      },
      {
        title: "Web Development Instructor",
        org: "AICITI Systems",
        date: "Instructor Role",
        badge: "Teaching",
        icon: "uil uil-presentation",
        highlights: [
          "Delivered hands-on learning with project-based teaching.",
          "Helped students build practical apps and understand workflows.",
        ],
      },
      {
        title: "Technical Team Member / Instructor",
        org: "Dominion University — Tech Team",
        date: "University Role",
        badge: "Community",
        icon: "uil uil-users-alt",
        highlights: [
          "Supported technical initiatives, events, and student dev activities.",
          "Contributed to a strong learning culture and peer mentoring.",
        ],
      },
    ],
    []
  );

  const items = toggleState === 1 ? education : experience;

  const openItem = (item) => {
    setActiveItem(item);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setActiveItem(null);
  };

  // ESC close + lock body scroll (like your skills modals)
  useEffect(() => {
    if (!isModalOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isModalOpen]);

  // Scroll reveal animation
  useEffect(() => {
    const root = sectionRef.current;
    if (!root) return;

    const nodes = root.querySelectorAll(".qualification__card");

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("is-visible");
        });
      },
      { threshold: 0.14 }
    );

    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [toggleState]);

  return (
    <section className="qualification section" ref={sectionRef}>
      <h2 className="section__title">Qualification</h2>
      <span className="section__subtitle">Education & Professional Experience</span>

      <div className="qualification__hint">
        <i className="uil uil-mouse-alt"></i>
        <span>Tap any item to view details</span>
        <i className="uil uil-arrow-right"></i>
      </div>

      <div className="qualification__container container">
        <div className="qualification__tabs" role="tablist" aria-label="Qualification tabs">
  <button
    type="button"
    role="tab"
    aria-selected={toggleState === 1}
    className={
      toggleState === 1
        ? "qualification__button qualification__active button--flex"
        : "qualification__button button--flex"
    }
    onClick={() => toggleTab(1)}
  >
    <i className="uil uil-graduation-cap qualification-icon"></i> Education
  </button>

  <button
    type="button"
    role="tab"
    aria-selected={toggleState === 2}
    className={
      toggleState === 2
        ? "qualification__button qualification__active button--flex"
        : "qualification__button button--flex"
    }
    onClick={() => toggleTab(2)}
  >
    <i className="uil uil-briefcase-alt qualification-icon"></i> Experience
  </button>
</div>


        <div className="qualification__sections">
          <div className="qualification__content qualification__content-active">
            {items.map((item, idx) => {
              const isLeft = idx % 2 === 0;
              const last = idx === items.length - 1;

              return (
                <div className="qualification__data" key={`${item.title}-${idx}`}>
                  {/* LEFT */}
                  {isLeft ? (
                    <button
                      type="button"
                      className="qualification__card"
                      onClick={() => openItem(item)}
                    >
                      <div className="qualification__cardTop">
                        <span className="qualification__badge">{item.badge}</span>
                        <i className={`${item.icon} qualification__cardIcon`}></i>
                      </div>

                      <h3 className="qualification__title">{item.title}</h3>
                      <span className="qualification__subtitle">{item.org}</span>

                      <div className="qualification__calendar">
                        <i className="uil uil-calendar-alt"></i> {item.date}
                      </div>

                      <span className="qualification__openHint">
                        View details <i className="uil uil-arrow-right"></i>
                      </span>
                    </button>
                  ) : (
                    <div />
                  )}

                  {/* CENTER LINE */}
                  <div className="qualification__center">
                    <span className="qualification__rounder"></span>
                    {!last && <span className="qualification__line"></span>}
                  </div>

                  {/* RIGHT */}
                  {!isLeft ? (
                    <button
                      type="button"
                      className="qualification__card"
                      onClick={() => openItem(item)}
                    >
                      <div className="qualification__cardTop">
                        <span className="qualification__badge">{item.badge}</span>
                        <i className={`${item.icon} qualification__cardIcon`}></i>
                      </div>

                      <h3 className="qualification__title">{item.title}</h3>
                      <span className="qualification__subtitle">{item.org}</span>

                      <div className="qualification__calendar">
                        <i className="uil uil-calendar-alt"></i> {item.date}
                      </div>

                      <span className="qualification__openHint">
                        View details <i className="uil uil-arrow-right"></i>
                      </span>
                    </button>
                  ) : (
                    <div />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* DETAILS MODAL */}
      {isModalOpen && activeItem && (
        <div className="qualificationModalOverlay" role="dialog" aria-modal="true">
          <div className="qualificationModalBackdrop" onClick={closeModal} />

          <div className="qualificationModal">
            <div className="qualificationModalHeader">
              <div>
                <div className="qualificationModalHeaderTop">
                  <span className="qualification__badge">{activeItem.badge}</span>
                  <i className={`${activeItem.icon} qualification__cardIcon`}></i>
                </div>

                <h3 className="qualificationModalTitle">{activeItem.title}</h3>
                <p className="qualificationModalOrg">{activeItem.org}</p>
                <p className="qualificationModalDate">
                  <i className="uil uil-calendar-alt"></i> {activeItem.date}
                </p>
              </div>

              <button
                className="qualificationModalClose"
                type="button"
                onClick={closeModal}
                aria-label="Close"
              >
                <i className="uil uil-multiply"></i>
              </button>
            </div>

            <div className="qualificationModalBody">
              <h4 className="qualificationModalSectionTitle">Highlights</h4>
              <ul className="qualificationModalList">
                {activeItem.highlights.map((h, i) => (
                  <li key={i}>
                    <i className="uil uil-check-circle"></i>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="qualificationModalFooter">
              <span className="qualificationModalHint">
                Tip: press <kbd>ESC</kbd> to close
              </span>
              <button className="qualificationModalPrimary" type="button" onClick={closeModal}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Qualification;
